/* iter-loom · Editor Proofread Inbox.
 *
 * Route `/loom/editor` — visible to any user with global role `editor`
 * (also admin). Shows every Loom manuscript currently drafting or in
 * review with a per-project chapter progress line so editors can pick
 * up work as it lands.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { PanelHeader } from './admin/_shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, Loader2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax  = axios.create({ baseURL: API, withCredentials: true });

const BUCKET_META = {
  contemp: { label: 'Contemp',  tone: 'bg-rose-100 text-rose-900' },
  dark:    { label: 'Dark',     tone: 'bg-red-100 text-red-900' },
  grey:    { label: 'Grey',     tone: 'bg-stone-200 text-stone-900' },
  scifi:   { label: 'Sci-Fi',   tone: 'bg-indigo-100 text-indigo-900' },
  sweet:   { label: 'Sweet',    tone: 'bg-amber-100 text-amber-900' },
};

export default function LoomEditorInboxPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const r = await ax.get('/loom/editor-inbox');
      setRows(r.data?.manuscripts || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="mx-auto h-full max-w-4xl overflow-y-auto p-6" data-testid="loom-editor-inbox">
      <PanelHeader
        title="Loom Editor Inbox"
        subtitle="Every Loom manuscript currently drafting or in review — pick one up to proofread before admin approval."
        onRefresh={refresh}
      />
      {error && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800" data-testid="loom-editor-error">
          {error}
        </div>
      )}
      {busy && !rows.length && (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      )}
      {!busy && !rows.length && !error && (
        <div className="rounded-md border border-dashed p-8 text-center text-[12px] text-muted-foreground">
          Nothing to proofread right now.
        </div>
      )}
      <div className="space-y-2">
        {rows.map((m) => (
          <div key={m.id}
               className="rounded-md border border-border bg-card p-3 text-[12.5px]"
               data-testid={`loom-editor-row-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-serif text-[13.5px]">{m.spec_title}</span>
                  <Badge className={`text-[10px] uppercase ${BUCKET_META[m.spec_bucket]?.tone || ''}`}>
                    {BUCKET_META[m.spec_bucket]?.label || m.spec_bucket}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
                  <span className="text-[11px] text-muted-foreground">
                    by <b>{m.pseudonym_name || m.pseudonym_id}</b>
                  </span>
                </div>
                <div className="mt-1 text-[11.5px] text-muted-foreground">
                  Progress: {m.chapters_drafted ?? 0} / {m.chapters_total ?? '?'} chapters drafted
                </div>
              </div>
              <Button size="sm" variant="outline" asChild
                      data-testid={`loom-editor-open-${m.id}`}>
                <a href={`/editor/${m.project_id}`}>Open in Editor</a>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
